import { Box, Grid, makeStyles, Typography } from '@material-ui/core';
import { PermIdentityOutlined } from '@material-ui/icons';
import {
  createCheckboxField,
  createSelectField,
  createTextField,
} from '../../../components/temp/componentBuiderTemp';
import { createNumberFormatFieldNoDecimalsAndWithLimit } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { EditControls } from '../../../components/editControls';
import { RoundedBackgroundIcon } from '../../../components/roundedBackgroundIcon';
import { BorrowerFormStore } from '../../../stores/borrowers/borrowerFormStore';
import {
  FORM_MAX_WIDTH,
  verifiedInvestmentPropertiesFFOptions,
  verifiedInvestmentPropertiesGUOptions,
  verifiedInvestmentPropertiesSPROptions
} from '../../../utils/constants';
import {
  Bankruptcy,
  BankruptcyDate,
  Foreclosure,
  ForeclosureDate,
} from '@roc/client-portal-shared/components';
import {
  EmailAutocompleteField,
  FirstNameAutocompleteField,
} from './borrowerInformationFields';
import { useStore } from '@roc/client-portal-shared/hooks';
import { useQuoteTypeStore } from '../../../hooks/useQuoteTypeStore';
import { PercentagePicker } from '../../leverage/components/leverageComponents';
import { LoanSubType } from '@roc/feature-utils';
import { citizenshipStatusOptionsRental } from '@roc/feature-loans';

const useStyles = makeStyles(theme => ({
  infoMessage: {
    fontStyle: 'italic',
    marginTop: '0rem',
    marginLeft: '1rem',
    color: 'gray',
    fontSize: '12px',
  },
}));

interface BorrowerInformationFormProps {
  store: BorrowerFormStore;
  onDeleteClick: (borrowerKey: string) => void;
  removeButtonDisabled?: boolean;
  showActions?: boolean;
  displayPGField: boolean;
  showTitle?: boolean;
}

export const BorrowerInformationForm = observer(
  ({
    store,
    onDeleteClick,
    showActions = true,
    removeButtonDisabled = false,
    displayPGField,
    showTitle = true,
  }: BorrowerInformationFormProps) => {
    const classes = useStyles();
    const { quoteTypeStore } = useQuoteTypeStore();
    const { borrowersStore, entityStore } = quoteTypeStore;
    const [borrowerHasExperienceRecord, setBorrowerHasExperienceRecord] = useState(false)
    const { showOwnershipPercentage } = borrowersStore;
    const { cannotCreateBorrower, isNewBorrower } = store;
    const { globalStore } = useStore();
    const { isBorrowerPercentageOwnershipEditable } = entityStore;
    const isTerm = [LoanSubType.SINGLE_PROPERTY, LoanSubType.RENTAL_PORTFOLIO].includes(quoteTypeStore.loanSubtype);
    function saveHandler() {
      const isValid = store.runFormValidationWithMessage(
        'Please complete all the required fields'
      );
      store.setShowErrors(true);
      if (isValid) {
        store.setEditMode(false);
      }
    }
    const getOptions = () => {
      switch (quoteTypeStore.loanSubtype) {
        case LoanSubType.SINGLE_PROPERTY:
        case LoanSubType.RENTAL_PORTFOLIO:
          return verifiedInvestmentPropertiesSPROptions;
        case LoanSubType.FIX_AND_FLIP:
        case LoanSubType.STABILIZED_BRIDGE:
          return verifiedInvestmentPropertiesFFOptions;
        case LoanSubType.GROUND_UP:
          return verifiedInvestmentPropertiesGUOptions
        default:
          return verifiedInvestmentPropertiesFFOptions
      }
    }

    const firstName = store.form.fields.firstName.value;
    const emailAddress = store.form.fields.emailAddress.value;
    const fundedBorrower = store.form.fields.projectsCompleted.value;
    const experience = store.form.fields.numberOfVerifiedInvestmentProperties.value;
    const hasSelectedRecord = Object.keys(store.selectedRecord || {}).length > 0;
    const numberOfVerifiedPropertiesOptions = LoanSubType.FIX_AND_FLIP === quoteTypeStore.loanSubtype || LoanSubType.STABILIZED_BRIDGE === quoteTypeStore.loanSubtype ? verifiedInvestmentPropertiesFFOptions : verifiedInvestmentPropertiesGUOptions
    useEffect(() => {
      if (experience) {
        store.onFieldChange('numberOfVerifiedInvestmentProperties', experience);
        setBorrowerHasExperienceRecord(Boolean(
          store?.selectedRecord['numberOfVerifiedInvestmentProperties']
        ))
      }
    }, [experience]);
    // Name and email search run independently until a record is selected. After select,
    // First Name locks; Email locks only when the selected record already has email;
    // search stays off while a record is selected; blank email remains free-text.
    // Shared borrowerOptions: last successful search wins the dropdown.
    useEffect(() => {
      if (hasSelectedRecord) {
        store.clearBorrowerOptions();
        return;
      }
      if (firstName) {
        store.clearBorrowerOptions();
        if (globalStore.userFeatures?.displayLeads) {
          store.findBorrowersAndLeadsByNameDebounced(firstName);
        } else {
          store.findBorrowersByNameDebounced(firstName);
        }
      } else if (!emailAddress || emailAddress.length < 3) {
        store.clearBorrowerOptions();
      }
    }, [firstName, emailAddress, hasSelectedRecord]);

    useEffect(() => {
      if (hasSelectedRecord) {
        store.clearBorrowerOptions();
        return;
      }
      if (emailAddress && emailAddress.length >= 3) {
        if (globalStore.userFeatures?.displayLeads) {
          store.findBorrowersAndLeadsByEmailDebounced(emailAddress);
        } else {
          store.findBorrowersByEmailDebounced(emailAddress);
        }
      } else if (!firstName) {
        store.clearBorrowerOptions();
      }
    }, [emailAddress, firstName, hasSelectedRecord]);

    return (
      <Grid container spacing={2} justifyContent="center" alignItems="center">
        {showTitle && (
          <Grid
            item
            container
            spacing={2}
            justifyContent="flex-start"
            alignItems="center"
            style={{ maxWidth: FORM_MAX_WIDTH, marginBottom: '0.5rem' }}
          >
            <Grid item>
              <RoundedBackgroundIcon
                Icon={PermIdentityOutlined}
                iconColor="#6D6D6D"
              />
            </Grid>
            <Grid item>
              <Typography variant="h4" style={{ color: '#6D6D6D' }}>
                Add Borrower
              </Typography>
            </Grid>
          </Grid>
        )}
        <Grid item container spacing={2} style={{ maxWidth: FORM_MAX_WIDTH }}>
          <Grid item xs={12} sm={6}>
            <FirstNameAutocompleteField
              store={store}
              optional
              loanSubtype={quoteTypeStore.loanSubtype}
              disabled={fundedBorrower || hasSelectedRecord}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'Last Name',
              fieldName: 'lastName',
              testId: 'lastName',
              store,
              standaloneLabel: false,
              disabled:
                fundedBorrower ||
                Boolean(store?.selectedRecord['lastName']) ||
                (cannotCreateBorrower && isNewBorrower),
              addMarkForRequiredFields: false,
            })}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" className={classes.infoMessage}>
              Adding a borrower at this stage is optional, but we will ask for one upon submission.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <EmailAutocompleteField
              store={store}
              loanSubtype={quoteTypeStore.loanSubtype}
              disabled={
                fundedBorrower ||
                Boolean(store?.selectedRecord['emailAddress'])
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {createNumberFormatFieldNoDecimalsAndWithLimit({
              label: 'Fico Score',
              fieldName: 'ficoScore',
              testId: 'ficoScore',
              store,
              standaloneLabel: false,
              showErrors: store.form.meta.showErrors || false,
              disabled: Boolean(store?.selectedRecord['ficoScore']) ||
                fundedBorrower,
              isRequired: true,
            })}
          </Grid>
          {isTerm && <Grid item xs={12} sm={6}>
            {createSelectField({
              store,
              testId: 'citizenshipStatus',
              fieldName: 'citizenshipStatus',
              label: 'Citizenship Status',
              options: citizenshipStatusOptionsRental,
              standaloneLabel: false,
              addMarkForRequiredFields: false,
              disabled: !Boolean(
                store?.selectedRecord['citizenshipStatus']
              )
                ? false
                : fundedBorrower || (cannotCreateBorrower && isNewBorrower),
              isRequired: true,
            })}
          </Grid>}
          <Grid item xs={12} sm={isTerm ? 12 : 6}>
            {createSelectField({
              store,
              testId: 'numberOfVerifiedInvestmentProperties',
              fieldName: 'numberOfVerifiedInvestmentProperties',
              label: 'Number of Verified Investment Properties',
              options: getOptions() as any,
              standaloneLabel: false,
              addMarkForRequiredFields: false,
              disabled: borrowerHasExperienceRecord && Boolean(experience),
              isRequired: true,
            })}
          </Grid>
          {isTerm && (
            <>
              <Grid item xs={store.form.fields.bankruptcy.value === 'Y' ? 6 : 12}>
                <Bankruptcy store={store} />
              </Grid>
              {store.form.fields.bankruptcy.value === 'Y' && (
                <Grid item xs={6}>
                  <BankruptcyDate store={store} />
                </Grid>
              )}
              <Grid item xs={store.form.fields.foreclosure.value === 'Y' ? 6 : 12}>
                <Foreclosure store={store} />
              </Grid>
              {store.form.fields.foreclosure.value === 'Y' && (
                <Grid item xs={6}>
                  <ForeclosureDate store={store} />
                </Grid>
              )}
            </>
          )}
          {displayPGField && (
            <Grid item xs={(showOwnershipPercentage || isTerm) ? 8 : 12}>
              <Grid container justifyContent="center" alignItems="center">
                <Grid item>
                  {createCheckboxField({
                    store,
                    fieldName: 'personalGuarantor',
                    label:
                      'This borrower is a personal guarantor for the loan.',
                    color: 'primary',
                  })}
                </Grid>
                <Grid item>
                  <Typography
                    variant="body2"
                    className={classes.infoMessage}
                    style={{ textAlign: 'center' }}
                  >
                    If yes, the borrower's credit and background reports are
                    needed. if no, only a background report is needed.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
          {(showOwnershipPercentage || isTerm) && (
            <Grid item xs={4}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                pb={1}
              >
                <PercentagePicker
                  label="Ownership Percentage"
                  fieldName="percentageOfOwnership"
                  value={store.form.fields.percentageOfOwnership.value}
                  onChange={val => {
                    store.onFieldChange('percentageOfOwnership', val)
                    if (val > 51) {
                      store.onFieldChange('personalGuarantor', true);
                    }
                  }}
                  disabled={!isBorrowerPercentageOwnershipEditable}
                />
              </Box>
              <Typography
                variant="body2"
                className={classes.infoMessage}
                style={{ textAlign: 'center' }}
              >
                *anyone 20% ownership or higher must personally gaurantee
              </Typography>
            </Grid>
          )}
          {showActions && (
            <Grid
              item
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
              style={{ padding: '1rem' }}
            >
              <EditControls
                onDeleteClick={() => onDeleteClick(store.key)}
                onSaveClick={saveHandler}
                removeButtonDisabled={removeButtonDisabled}
                onResetClick={() => store.reset()}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  }
);
